<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.personName" placeholder="人员姓名" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.personIdNumber" placeholder="身份证号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.personTel" placeholder="手机号" clearable @keydown="onEnter" />
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>
    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData">
      <el-table-column align="left" prop="personName" label="人员姓名" :min-width="120" />
      <el-table-column align="left" prop="personIdNumber" label="身份证号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="personTel" label="手机号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="personBank" label="银行卡号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="center" prop="authStatus" label="是否实名" :min-width="80">
        <template #default="scope">
          {{ isRealNameLabel[scope.row.authStatus] }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="contractStatus" label="自雇者协议" :min-width="120">
        <template #default="scope">
          <el-button v-if="scope.row.contractStatus == EAgreementStatusType.SIGNED" type="text" @click="preview(scope.row)">
            查看
          </el-button>
          <span v-else>{{ agreementStatusLabel[scope.row.contractStatus] || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column align="left" prop="type" label="接包方式" :min-width="120">
        <template #default="scope">
          {{ acceptPackageTypeLabel[scope.row.type] }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="orderInfo" label="关联订单" :min-width="120" show-overflow-tooltip />

      <el-table-column
        v-if="buttons.length > 0 && from=='company'"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        :width="160"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <download-receipt :visible="downloadVisible" :row="currentRow" @close="()=>{downloadVisible = false;}" />
  </content-container>
</template>

<script setup lang="ts">
import { reactive, ref, PropType } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
} from '../../../../../main';
import { isRealNameLabel, IDutyAcceptPackageParams, IDutyAcceptPackage, acceptPackageTypeLabel, EAgreementStatusType, agreementStatusLabel } from '@core/main/types';
import DownloadReceipt from './dialog/DownloadReceipt.vue';
import { useRoute } from 'vue-router';
import { queryDutyAcceptPackagePage } from '@core/main/api/crowd-source/task-accept-package';

defineOptions({ personName: 'ReceiveBag' });

defineProps({
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});

const route = useRoute();
const defaultPageSize = 12;
const params = reactive<IDutyAcceptPackageParams>({
  page: 1,
  size: defaultPageSize,
  dutyId: Number(route.query.taskId),
  dutyType: 0,
});
const loading = ref(false);
const total = ref(0);
const tableData = ref<IDutyAcceptPackage[]>([]);

const downloadVisible = ref(false);
const currentRow = ref<IDutyAcceptPackage>({} as IDutyAcceptPackage);

const { buttons } = useTblPermissionButtons([
  {
    name: '下载回单',
    code: 'duty:accept:package:receipt',
    action: download,
  },
]);

async function fetchData() {
  loading.value = true;

  try {
    const res = await queryDutyAcceptPackagePage(params);

    tableData.value = res.data;
    total.value = res.total;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size', 'dutyId', 'dutyType'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

async function download(row: any) {
  currentRow.value = row;
  downloadVisible.value = true;
}

function preview(row) {
  window.open(row.selfEmployedContractPreviewUrl);
}

fetchData();
</script>
